/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { MerchantDetailsParentComponent } from "../../merchant-details-parent.component";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Merchant } from "../../../../models";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService, SharedFormService } from "../../../../services";
import {
  ACH_AMEX_COMBINED_DATA,
  ACH_AMEX_DIRECT_BATCHING_COMBINED_DATA,
  ACH_DIRECT_BATCHING_COMBINED_DATA,
  ACH_ECHECK_DATA,
  AMEX_DATA,
  AMEX_DIRECT_BATCHING_COMBINED_DATA,
  CREDIT_CARD_CONSTANTS,
  DIRECT_BATCHING_DATA,
  MERCHANT_MIGRATION_STATUS,
  PATTERN_CONSTANTS,
  TOKEN_CONVERSION_DATA
} from "../../../../utils/data.rest.constants";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { TokenConversionConfirmationModelComponent } from "../general-info-edit/token-conversion-confirmation-model/token-conversion-confirmation-model.component";
import {
  ConfirmationDialogData,
  EffectiveDateWithAssociatedMerchants,
  MigrationConfirmationModal,
  TokenConversionData
} from "../../../../models/TokenModel";
import { ConfirmationDialogComponent } from "../../../confirmation-dialog/confirmation-dialog.component";
import * as moment from "moment/moment";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-payment-processor-general-info",
  templateUrl: "./payment-processor-general-info.component.html"
})
export class PaymentProcessorGeneralInfoComponent extends MerchantDetailsParentComponent implements OnInit {
  merchantDetailsForm!: FormGroup;
  editable = false;
  actionInProgress = false;
  private pendingFlagChanges: { achEcheckEnabled?: boolean; amexEnabled?: boolean; directBatching?: boolean } | null =
    null;
  buttonName = "Update Merchant Details";
  @Input("loaded") loaded = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private sharedFormService: SharedFormService,
    private fb: FormBuilder,
    public modalRef: BsModalRef,
    private modalService: BsModalService,
    private toastr: ToastrService
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    if (this.merchant.merchantMigrationDetails?.toBeMigrated) {
      this.merchantService.merchantMigrationDetails = this.merchant.merchantMigrationDetails;
    }
    this.createForm();
  }

  createForm() {
    const isGlobalPayments = this.merchantService.isGlobalPayments(this.merchant);
    this.merchantDetailsForm = this.fb.group({
      generalInfo: this.fb.group({
        merchantId: [{ value: this.merchant.id, disabled: true }],
        merchantName: [this.merchant.merchantName, [Validators.required, Validators.maxLength(50)]],
        externalMerchantId: [this.merchant.externalMerchantId, [Validators.maxLength(12)]],
        customerAccountNumber: [this.merchant.customerAccountNumber, [Validators.required]],
        instaMedConversionStatus: [false],
        previousMMID: ["", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)]],
        suppressZeroDollarPayment: [
          { value: this.merchant.suppressZeroBalancePayment, disabled: this.authService.hasProvisionRole() }
        ],
        achEcheckEnabled: [
          {
            value: this.merchant.achEcheckEnabled !== false, // default true
            disabled: isGlobalPayments // Non-editable for Global Payments
          }
        ],
        amexEnabled: [
          {
            value: this.merchant.amexEnabled === true, // default false
            disabled: isGlobalPayments // Non-editable for Global Payments
          }
        ],
        ngepmStatement: [
          {
            value: this.merchant.ngepmStatement,
            disabled: this.authService.hasProvisionRole()
          }
        ],
        balanceIncludesBadDebt: [
          {
            value: this.merchant.balanceIncludesBadDebt,
            disabled: false
          }
        ],
        directBatching: [
          {
            value: this.merchant.directBatching,
            disabled: this.authService.hasProvisionRole()
          }
        ]
      })
    });
    this.sharedFormService.addCheckboxes(this.acceptedCardsFormGroup, this.merchant);
  }

  isInstaMed(): boolean {
    return this.merchantService.isInstaMed(this.merchant);
  }

  isAmexFeeRequired(): boolean {
    const merchant = this.merchantService.merchant;
    const isAmexCard = this.acceptedCardsFormGroup?.get(CREDIT_CARD_CONSTANTS.CREDIT_CARDS.AMEX.code)?.value;
    const amexPaymentFee = merchant?.contractedRates?.amexPercent;
    return (this.isInstaMed() || this.isWorldPay()) && isAmexCard && amexPaymentFee === null;
  }

  private hasAchEcheckChanged(): boolean {
    const originalValue = this.merchant.achEcheckEnabled !== false;
    const currentValue = this.generalInfoFormGroup.controls?.achEcheckEnabled?.value;
    return originalValue !== currentValue;
  }

  private revertAchEcheckValue(): void {
    const originalValue = this.merchant.achEcheckEnabled !== false;
    this.generalInfoFormGroup.controls?.achEcheckEnabled?.setValue(originalValue, { emitEvent: false });
  }

  private hasAmexChanged(): boolean {
    const originalValue = this.merchant.amexEnabled === true;
    const currentValue = this.generalInfoFormGroup.controls?.amexEnabled?.value;
    return originalValue !== currentValue;
  }

  private revertAmexValue(): void {
    const originalValue = this.merchant.amexEnabled === true;
    this.generalInfoFormGroup.controls?.amexEnabled?.setValue(originalValue, { emitEvent: false });
  }

  private hasDirectBatchingChanged(): boolean {
    const originalValue = this.merchant.directBatching;
    const currentValue = this.generalInfoFormGroup.controls?.directBatching?.value;
    return originalValue !== currentValue;
  }

  private revertDirectBatchingValue(): void {
    const originalValue = this.merchant.directBatching;
    this.generalInfoFormGroup.controls?.directBatching?.setValue(originalValue, { emitEvent: false });
  }

  private revertPendingFlagChanges(): void {
    if (!this.pendingFlagChanges) return;
    if (this.pendingFlagChanges.achEcheckEnabled !== undefined) {
      this.merchant.achEcheckEnabled = this.pendingFlagChanges.achEcheckEnabled;
      this.revertAchEcheckValue();
    }
    if (this.pendingFlagChanges.amexEnabled !== undefined) {
      this.merchant.amexEnabled = this.pendingFlagChanges.amexEnabled;
      this.revertAmexValue();
    }
    if (this.pendingFlagChanges.directBatching !== undefined) {
      this.merchant.directBatching = this.pendingFlagChanges.directBatching;
      this.revertDirectBatchingValue();
    }
    this.pendingFlagChanges = null;
  }

  updateGeneralInfo(): void {
    const isDirectBatchingEnabled = this.generalInfoFormGroup.controls?.directBatching?.value;
    const siteId = this.generalInfoFormGroup.controls?.siteId?.value;
    if (isDirectBatchingEnabled && !siteId) {
      this.toastr.warning(
        "Update failed: When Direct Batching is enabled, merchant details cannot be updated if no practice is associated. Please associate a practice or disable Direct Batching before updating merchant details."
      );
      return;
    }

    if (this.hasAchEcheckChanged() || this.hasAmexChanged() || this.hasDirectBatchingChanged()) {
      this.handlePracticeLevelFlagUpdate();
      return;
    }

    if (
      this.generalInfoFormGroup.controls?.tokenConversionDate?.value &&
      this.generalInfoFormGroup.controls?.tokenConversionDate?.value !==
        this.merchantService.merchant.merchantMigrationDetails?.effectiveDate
    ) {
      const practiceId = this.generalInfoFormGroup.controls.externalMerchantId.value;
      if (!practiceId) {
        this.toastr.warning("Please add practice ID for this merchant before proceeding with migration.");
        return;
      }
      this.actionInProgress = true;
      this.merchantService.getMerchantsAssociatedWithPractice(practiceId).subscribe((data) => {
        let confirmationDetails: MigrationConfirmationModal;
        if (data.error) {
          confirmationDetails = {
            ...data,
            title: "InstaMed Merchant(s) Not Found",
            displayButtons: {
              showCancelBtn: true,
              showConfirmBtn: false
            }
          };
        } else {
          confirmationDetails = {
            ...data,
            title: "Scheduled Date Confirmation",
            errorMessage: `We found total of ${
              data.merchantMigrationSummaries.length
            } merchant(s) associated with practice ${practiceId}. 
              All the merchants will migrate on the scheduled date ${moment(
                this.generalInfoFormGroup.controls?.tokenConversionDate?.value
              ).format("MM/DD/YYYY")}.`,
            displayButtons: {
              showCancelBtn: true,
              showConfirmBtn: true
            }
          };
        }
        this.displayMigrationConfirmationModal(confirmationDetails);
      });
    } else {
      this.updateGeneralInfoApi();
    }
  }

  private handlePracticeLevelFlagUpdate(): void {
    const achChanged = this.hasAchEcheckChanged();
    const amexChanged = this.hasAmexChanged();
    const directBatchingChanged = this.hasDirectBatchingChanged();
    const constants =
      achChanged && amexChanged && directBatchingChanged
        ? ACH_AMEX_DIRECT_BATCHING_COMBINED_DATA
        : achChanged && amexChanged
        ? ACH_AMEX_COMBINED_DATA
        : achChanged && directBatchingChanged
        ? ACH_DIRECT_BATCHING_COMBINED_DATA
        : achChanged
        ? ACH_ECHECK_DATA
        : amexChanged && directBatchingChanged
        ? AMEX_DIRECT_BATCHING_COMBINED_DATA
        : amexChanged
        ? AMEX_DATA
        : DIRECT_BATCHING_DATA;

    // Resolve the authoritative PM Practice ID from the backend (it is resolved from the SG Practice ID
    // when not stored locally) rather than trusting the possibly-stale value on the merchant in memory.
    this.actionInProgress = true;
    this.merchantService.getMerchantPmPractice(this.merchant.id).subscribe(
      (ref) => {
        const siteId = ref?.siteId;
        const enterpriseId = ref?.enterpriseId;
        const practiceId = ref?.practiceId;

        if (!siteId || !enterpriseId || !practiceId) {
          this.actionInProgress = false;
          this.updateGeneralInfoApi();
          return;
        }

        // Propagate the resolved PM Practice ID so the save (and its backend cascade) groups by the
        // correct practice.
        this.applyResolvedPractice(siteId, enterpriseId, practiceId);

        this.merchantService.getMerchantsByClientDetails(siteId, enterpriseId, practiceId).subscribe(
          (results) => {
            // When only Direct Batching is changed, include the current merchant in the list
            const siblingMerchants =
              directBatchingChanged && !achChanged && !amexChanged
                ? results
                : results.filter((m) => m.mmid !== this.merchant.id);

            siblingMerchants.sort((a, b) => a.mmid - b.mmid);

            if (siblingMerchants.length === 0) {
              this.updateGeneralInfoApi();
              return;
            }

            this.actionInProgress = false;

            let dialogData: ConfirmationDialogData;

            // For Direct Batching, construct dynamic message with enable/disable action
            // Only apply special handling when Direct Batching is toggled independently
            if (directBatchingChanged && !achChanged && !amexChanged) {
              const newValue = this.generalInfoFormGroup.controls?.directBatching?.value;
              const action = newValue ? "Enable" : "Disable";
              const postMessage = `Please confirm you want to proceed with this update to ${action} Direct Batching.`;

              // Check if any merchants have location aliases to determine if we should show Location Alias column
              const hasLocations = siblingMerchants.some((m) => m.locationAlias || m.locationName);
              const tableHeaders = hasLocations
                ? DIRECT_BATCHING_DATA.TABLE_HEADERS
                : DIRECT_BATCHING_DATA.TABLE_HEADERS.slice(0, 2);

              dialogData = {
                title: DIRECT_BATCHING_DATA.TITLE,
                message: DIRECT_BATCHING_DATA.MESSAGE,
                postMessage: postMessage,
                tableHeaders: tableHeaders,
                tableRows: siblingMerchants.map((m) => {
                  const row = [m.mmid.toString(), m.merchantName];
                  if (hasLocations && (m.locationAlias || m.locationName)) {
                    row.push(m.locationAlias || m.locationName);
                  }
                  return row;
                }),
                confirmButtonText: DIRECT_BATCHING_DATA.CONFIRM_BUTTON_TEXT,
                cancelButtonText: DIRECT_BATCHING_DATA.CANCEL_BUTTON_TEXT
              };
            } else {
              // For combined cases (Direct Batching + ACH/AMEX), use the constants from the ternary
              // but include Location Alias column if Direct Batching is part of the change
              const hasLocations =
                directBatchingChanged && siblingMerchants.some((m) => m.locationAlias || m.locationName);
              const tableHeaders = hasLocations ? constants.TABLE_HEADERS : constants.TABLE_HEADERS.slice(0, 2);

              dialogData = {
                title: constants.TITLE,
                message: constants.MESSAGE,
                tableHeaders: tableHeaders,
                tableRows: siblingMerchants.map((m) => {
                  const row = [m.mmid.toString(), m.merchantName];
                  if (hasLocations && (m.locationAlias || m.locationName)) {
                    row.push(m.locationAlias || m.locationName);
                  }
                  return row;
                }),
                confirmButtonText: constants.CONFIRM_BUTTON_TEXT,
                cancelButtonText: constants.CANCEL_BUTTON_TEXT
              };
            }

            this.displayPracticeLevelConfirmationModal(dialogData, achChanged, amexChanged, directBatchingChanged);
          },
          () => {
            this.actionInProgress = false;
            this.toastr.error(constants.ERROR_RETRIEVE);
            if (achChanged) this.revertAchEcheckValue();
            if (amexChanged) this.revertAmexValue();
            if (directBatchingChanged) this.revertDirectBatchingValue();
          }
        );
      },
      () => {
        this.actionInProgress = false;
        this.toastr.error(constants.ERROR_RETRIEVE);
        if (achChanged) this.revertAchEcheckValue();
        if (amexChanged) this.revertAmexValue();
        if (directBatchingChanged) this.revertDirectBatchingValue();
      }
    );
  }

  private applyResolvedPractice(siteId: string, enterpriseId: string, practiceId: string): void {
    this.merchant.siteId = siteId;
    this.merchant.enterpriseId = enterpriseId;
    this.merchant.practiceId = practiceId;
    // The save reads these form controls (added by the edit component) to build the PUT body, so the
    // resolved PM Practice ID must be written here too. patchValue ignores controls that are absent.
    this.generalInfoFormGroup.patchValue(
      { siteId, enterpriseId, enterprisePracticeId: practiceId },
      { emitEvent: false }
    );
  }

  private displayPracticeLevelConfirmationModal(
    dialogData: ConfirmationDialogData,
    achChanged: boolean,
    amexChanged: boolean,
    directBatchingChanged: boolean
  ): void {
    const modalOptions: ModalOptions = {
      initialState: { dialogData },
      backdrop: "static",
      keyboard: false
    };
    this.modalRef = this.modalService.show(ConfirmationDialogComponent, modalOptions);
    this.modalRef.content.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.pendingFlagChanges = {};
        if (achChanged) {
          this.pendingFlagChanges.achEcheckEnabled = this.merchant.achEcheckEnabled;
          this.merchant.achEcheckEnabled = this.generalInfoFormGroup.controls?.achEcheckEnabled?.value;
        }
        if (amexChanged) {
          this.pendingFlagChanges.amexEnabled = this.merchant.amexEnabled;
          this.merchant.amexEnabled = this.generalInfoFormGroup.controls?.amexEnabled?.value;
        }
        if (directBatchingChanged) {
          this.pendingFlagChanges.directBatching = this.merchant.directBatching;
          this.merchant.directBatching = this.generalInfoFormGroup.controls?.directBatching?.value;
        }
        this.updateGeneralInfoApi();
      } else {
        if (achChanged) this.revertAchEcheckValue();
        if (amexChanged) this.revertAmexValue();
        if (directBatchingChanged) this.revertDirectBatchingValue();
      }
    });
  }

  isUpdateGeneralInfoValid(): boolean {
    return (
      this.merchantDetailsForm.valid &&
      (!this.generalInfoFormGroup.controls?.instaMedConversionStatus?.value ||
        (this.generalInfoFormGroup.controls?.instaMedConversionStatus?.value &&
          this.merchantService.isPracticeIdMatched(this.generalInfoFormGroup.controls.externalMerchantId.value)))
    );
  }

  updateGeneralInfoApi() {
    if (this.isAmexFeeRequired()) {
      this.acceptedCardsFormGroup
        .get(CREDIT_CARD_CONSTANTS.CREDIT_CARDS.AMEX.code)
        ?.setErrors({ invalidAmexFee: true });
    }
    if (this.isUpdateGeneralInfoValid()) {
      this.actionInProgress = true;
      const tempMerchant: Merchant = JSON.parse(JSON.stringify(this.merchant));
      this.sharedFormService.updateMerchantGeneralInfo(tempMerchant, this.generalInfoFormGroup);
      this.sharedFormService.updatedAcceptedCreditCards(tempMerchant, this.acceptedCardsFormGroup);
      this.setFeeDetailsToNullIfNoneExist(tempMerchant);
      this.merchantService.updateMerchantGeneralInfo(tempMerchant).subscribe(
        (merchant) => {
          this.merchant = merchant;
          this.merchantService.merchant = this.merchant;
          this.pendingFlagChanges = null;
          this.editable = false;
          this.actionInProgress = false;
          this.merchantDetailsForm.controls["previousMMID"]?.removeValidators(Validators.required);
          if (
            this.generalInfoFormGroup.controls?.tokenConversionDate?.value &&
            !this.authService.hasProvisionRole() &&
            this.merchant.merchantMigrationDetails?.migrationStatus !== MERCHANT_MIGRATION_STATUS.COMPLETE &&
            this.merchant.merchantMigrationDetails?.migrationStatus !== MERCHANT_MIGRATION_STATUS.IN_PROGRESS
          ) {
            const confirmedDetails: TokenConversionData = {
              title: "Confirmed",
              message: {
                mainMessage: `Patient credit card tokens confirmed for conversion on ${moment(
                  tempMerchant.merchantMigrationDetails?.effectiveDate
                ).format("MM/DD/YYYY")}.`
              },
              displayButtons: {
                showCancelBtn: true,
                showConfirmBtn: false
              }
            };
            this.displayTokenConversionModal(confirmedDetails);
          }
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
          this.revertPendingFlagChanges();
        }
      );
    } else {
      this.merchantDetailsForm.markAllAsTouched();
    }
  }

  displayTokenConversionModal(confirmationDetails: TokenConversionData) {
    const tokenConversionConfirmationDetails: ModalOptions = {
      initialState: {
        modalData: confirmationDetails
      },
      ignoreBackdropClick: false,
      keyboard: false
    };
    this.modalRef = this.modalService.show(
      TokenConversionConfirmationModelComponent,
      tokenConversionConfirmationDetails
    );
    this.modalRef.content.onClose.subscribe((result: any) => {
      if (result && confirmationDetails.title === TOKEN_CONVERSION_DATA.TITLE) {
        this.updateGeneralInfoApi();
      } else {
        this.actionInProgress = false;
      }
    });
  }

  displayMigrationConfirmationModal(modalData: MigrationConfirmationModal) {
    const migrationConfirmationDetails: ModalOptions = {
      initialState: {
        modalData
      },
      ignoreBackdropClick: true,
      keyboard: false
    };
    this.modalRef = this.modalService.show(TokenConversionConfirmationModelComponent, migrationConfirmationDetails);
    this.modalRef.content.onClose.subscribe((result: any) => {
      if (result) {
        const data: EffectiveDateWithAssociatedMerchants = {
          effectiveDate: moment(this.generalInfoFormGroup.controls?.tokenConversionDate?.value).format("YYYY-MM-DD"),
          merchantMigrationSummaries: modalData.merchantMigrationSummaries
        };
        this.merchantService.updateEffectiveDateToAssociatedMerchants(data).subscribe(() => {
          const confirmationDetails: TokenConversionData = {
            title: TOKEN_CONVERSION_DATA.TITLE,
            message: {
              mainMessage: TOKEN_CONVERSION_DATA.MESSAGE.MAIN_MESSAGE,
              submessages: TOKEN_CONVERSION_DATA.MESSAGE.SUB_MESSAGES
            },
            displayButtons: {
              showCancelBtn: true,
              showConfirmBtn: true
            }
          };
          this.displayTokenConversionModal(confirmationDetails);
        });
      } else {
        this.actionInProgress = false;
      }
    });
  }

  setEditable(value: boolean) {
    this.editable = value;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }

  get generalInfoFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["generalInfo"] as FormGroup;
  }

  get acceptedCardsFormGroup(): FormGroup {
    return this.merchantDetailsForm.controls["acceptedCards"] as FormGroup;
  }
}
